import { Navigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import { DashboardModes, getDashboardMode } from 'utils/dashboardMode';
import { FC, useEffect, useMemo, useState } from 'react';
import Loader from './Loader';
import axios from 'axios';


interface UserInformation {
  id: string;
  email: string;
  userType: 'SuperAdmin' | 'Consultant' | 'User';
  city?: any; 
  Consultant?: {
    isConsultant?: boolean;
  };
  isConsultant?: boolean;
}

const useAuthHook = useAuth as () => { user: UserInformation | null; isInitialized?: boolean };

const parseStoredUser = (): UserInformation | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem('userInformation');
    return stored ? (JSON.parse(stored) as UserInformation) : null;
  } catch (error) {
    console.warn('Failed to parse stored user information', error);
    return null;
  }
};

const DefaultDashboardRedirect: FC = () => {
  const { user, isInitialized } = useAuthHook();
  const storedUser = useMemo(() => parseStoredUser(), []);
  const baseUser = user || storedUser || null;
  const [resolvedUser, setResolvedUser] = useState<UserInformation | null>(baseUser);
  const [loadingInfo, setLoadingInfo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const hasConsultantHint =
      baseUser?.Consultant?.isConsultant !== undefined ||
      baseUser?.isConsultant !== undefined ||
      String(baseUser?.userType || '').toLowerCase().includes('consult');

    if (hasConsultantHint) {
      setResolvedUser(baseUser);
      return;
    }

    const fetchInfo = async () => {
      setLoadingInfo(true);
      try {
        const resp = await axios.get('/api/user-info');
        if (cancelled) return;
        const fetched = resp.data as UserInformation;
        setResolvedUser(fetched);
        window.localStorage.setItem('userInformation', JSON.stringify(fetched));
      } catch (error) {
        console.error('Failed to fetch user-info for redirect', error);
        setResolvedUser(baseUser);
      } finally {
        if (!cancelled) setLoadingInfo(false);
      }
    };

    fetchInfo();
    return () => {
      cancelled = true;
    };
  }, [baseUser]);

  if (!isInitialized || loadingInfo || !resolvedUser) {
    return <Loader />;
  }

  const activeUser = resolvedUser as Partial<UserInformation>;

  const isSuperAdmin = activeUser.userType === 'SuperAdmin';
  const userTypeRaw = String(activeUser?.userType || '').toLowerCase();
  const isConsultant = Boolean(
    activeUser?.Consultant?.isConsultant ||
      activeUser?.isConsultant ||
      userTypeRaw === 'consultant' ||
      userTypeRaw.includes('consult')
  );

  const hasActiveCity = Boolean(activeUser?.city);

  const storedMode = typeof window === 'undefined' ? null : getDashboardMode();

  const normalizedMode: any | null = (() => {
    if (!storedMode) {
      return null;
    }

    const mode = storedMode as any;

    if (mode === DashboardModes.CITY) {
      if (isSuperAdmin || isConsultant) return null;
      return hasActiveCity ? DashboardModes.CITY : null;
    }
    if (mode === DashboardModes.SUPER_ADMIN) {
      return isSuperAdmin ? DashboardModes.SUPER_ADMIN : null;
    }
    if (mode === DashboardModes.CONSULTANT) {
      return isConsultant ? DashboardModes.CONSULTANT : null;
    }
    return null;
  })();

  let target = '/overview';

  if (normalizedMode === DashboardModes.SUPER_ADMIN) {
    target = '/super-admin';
  } else if (normalizedMode === DashboardModes.CONSULTANT) {
    target = '/consultant';
  } else if (normalizedMode === DashboardModes.CITY) {
    target = '/overview';
  } else if (isSuperAdmin) {
    target = '/super-admin';
  } else if (isConsultant) {
    target = '/consultant';
  } else if (hasActiveCity) {
    target = '/overview';
  }

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('DefaultDashboardRedirect', {
      isConsultant,
      isSuperAdmin,
      hasActiveCity,
      storedMode,
      target,
      activeUser
    });
  }, [isConsultant, isSuperAdmin, hasActiveCity, storedMode, target, activeUser]);

  return <Navigate to={target} replace />;
};

export default DefaultDashboardRedirect;
